/**
 * ================================================================================================
 *  ⓘ — THE EXPLANATION IS KEPT, AND IT STOPS CROWDING THE BOX. **2026-10-25.**
 * ================================================================================================
 *
 * The Owner, looking at `הרשאות לחברות` and `חברות בארגון`:
 *
 *     *"עכשיו יש המון הערות ביניהן בתוך הקוביות, האם זה רק בשבילי ... או שיהיה עם סימן של I"*
 *
 * The answer to the first half is that it is PRODUCT COPY, not development output: it ships, and a
 * real office reads it. Several of those sentences exist because the rule they state is one every
 * other product gets wrong — *"אין רשימת חסימה"* is on the grant screen because "admin sees
 * everything" is the assumption it has to contradict.
 *
 * The answer to the second half is this component. The sentence is not deleted and it is not
 * shortened into something less true; it moves behind a marker, so the box shows its heading and
 * its action, and the explanation is one press away for whoever wants it.
 *
 * ── IT IS A `<details>`, AND THAT IS THE WHOLE IMPLEMENTATION ───────────────────────────────────
 *
 * A native disclosure is keyboard-operable, announced as one, and needs no state of its own. The
 * alternatives — a tooltip on hover, or a div with an onClick — are the two ways this is usually
 * built and both are worse: a hover tooltip is unreachable on a touch screen and unreadable to a
 * screen reader, and a div needs `tabIndex`, `role`, `aria-expanded` and two key handlers to equal
 * what the browser already does.
 *
 * ── WHEN NOT TO USE IT ──────────────────────────────────────────────────────────────────────────
 *
 * Anything a person must read BEFORE acting stays in the open: a refusal, a consequence, a warning
 * about what an action cannot be undone from. Hiding one of those behind a marker is how a product
 * gets to claim it told somebody. This is for the standing explanation of a screen's rules — true,
 * worth having, and not news every time you arrive.
 */

import type { ReactNode } from 'react';

export interface InfoNoteProps {
  /** The short line that stays visible. Omitted ⇒ the marker alone. */
  readonly summaryHe?: string;
  /** The explanation, revealed on request. */
  readonly children: ReactNode;
}

export function InfoNote({ summaryHe, children }: InfoNoteProps): JSX.Element {
  return (
    <details className="info-note">
      <summary className="info-note__summary">
        {/*
          The marker is decorative: the summary text names what the explanation is about, and a
          screen reader that also read "information icon" would be reading the picture twice. When
          there is no summary text the accessible name comes from `aria-label` on the summary.
        */}
        <span className="info-note__marker" aria-hidden="true">
          ⓘ
        </span>
        {summaryHe === undefined ? (
          <span className="visually-hidden">הסבר</span>
        ) : (
          <span className="info-note__label">{summaryHe}</span>
        )}
      </summary>
      <div className="info-note__body">{children}</div>
    </details>
  );
}
